import React, { useEffect, useRef, useState } from "react"
import { useStore } from "../../../stores/RootStoreContext"
import { IClientRequestDto } from "../../../types/request/IClientRequestDto"
import { CommonInput } from "./fields/CommonInput"
import { IErrorModel } from "../../../types/error/IErrorModel"
import { CountryPicker } from "./fields/CountryPicker"
import { ICountry } from "../../../types/dict/ICountry"
import { CurrencyPicker } from "./fields/CurrencyPicker"
import * as TrinityAPI from "../../../requests/TrinityAPI"
import { getFieldError, round2Digits, spacedNum } from "../CommonHelper"
import { IInnRegistryDto } from "../../../types/inn/IInnRegistryDto"
import { IClientRequestStatusDto } from "../../../types/request/IClientRequestStatusDto"
import { IPageResponse } from "../../../types/IPageResponse"
import { IClientContractorDto } from "../../../types/contractor/IClientContractorDto"
import { useClickAway } from "react-use"

export const RequestPopUp: React.FC<{
  visible: boolean
  setVisible: (visible: boolean) => void
  id?: number | undefined
}> = ({ visible, setVisible, id }) => {
  const { userStore } = useStore()

  const [innList, setInnList] = useState<IPageResponse<IInnRegistryDto>>()
  const [contractors, setContractors] = useState<IClientContractorDto[]>([])
  const [clientRequest, setClientRequest] = useState<IClientRequestDto>()
  const [innRegistry, setInnRegistry] = useState<IInnRegistryDto>()
  const [comment, setComment] = useState<string | undefined>()
  const [errors, setErrors] = useState<IErrorModel>()
  const [sending, setSending] = useState(false)

  const modalRef = useRef(null)

  useEffect(() => {
    if (id) {
      TrinityAPI.getClientRequest(id, response => {
        if (response.status === 200) {
          setClientRequest(response.data)
          setComment(response.data.comment)
        } else {
          setErrors(errors)
        }
      })
    }
  }, [id])

  useEffect(() => {
    TrinityAPI.getClientInnList(0, "", response => {
      if (response.status === 200) {
        const items = response.data.items.filter((i: IInnRegistryDto) => i.status === "APPROVED")
        setInnList({ ...innList, items: items, totalPages: response.data.totalPages })
      }
    })
    TrinityAPI.getClientContractors(response => {
      setContractors(response.data)
    })
  }, [])

  useEffect(() => {
    if (clientRequest && clientRequest.inn && (userStore.isManager || userStore.isAdmin)) {
      TrinityAPI.getInnRegistryByInn(clientRequest.inn, response => {
        if (response.status === 200) {
          setInnRegistry(response.data)
        }
      })
    }
  }, [clientRequest])

  const changeRecipientCountry = (countryList: ICountry[]) => {
    if (countryList.length === 0) {
      setClientRequest({ ...clientRequest, recipientCountry: undefined })
    } else {
      setClientRequest({ ...clientRequest, recipientCountry: countryList[0] })
    }
  }

  const changeRecipientBankCountry = (countryList: ICountry[]) => {
    if (countryList.length === 0) {
      setClientRequest({ ...clientRequest, recipientBankCountry: undefined })
    } else {
      setClientRequest({ ...clientRequest, recipientBankCountry: countryList[0] })
    }
  }

  // const handleInnChange = (inn: IInnSuggestion | undefined) => {
  //   if (inn && inn.suggestions.length > 0) {
  //     setClientRequest({
  //       ...clientRequest,
  //       inn: inn.suggestions[0].data.inn,
  //       clientCompanyName: inn.suggestions[0].value
  //     })
  //   }
  // }

  const handleInnSelectChange = (inn: string) => {
    if (inn) {
      const pickedInn = innList?.items.filter(i => i.inn === inn)[0]
      if (pickedInn) {
        setClientRequest({
          ...clientRequest,
          inn: pickedInn.inn,
          clientCompanyName: pickedInn.name,
          address: pickedInn.address
        })
      } else {
        setClientRequest({ ...clientRequest, inn: "", clientCompanyName: "", address: "" })
      }
    }
  }

  const handleContractorSelectChange = (id: string) => {
    if (id) {
      const pickedContractor = contractors.filter(c => c.id === id)[0]
      if (pickedContractor) {
        setClientRequest({
          ...clientRequest,
          recipientCountry: pickedContractor.country,
          recipientBankCountry: pickedContractor.country
        })
      }
    }
  }

  const submit = () => {
    if (!clientRequest || sending) return

    setSending(true)
    const request = { ...clientRequest }
    request.invoicePrice = round2Digits(Number(request.invoicePrice?.replaceAll(" ", "")) * 100).toString()
    TrinityAPI.createClientRequest(request, response => {
      if (response.status === 200) {
        window.location.href = "/request?status=NEW"
      } else {
        setErrors(response.data)
        setSending(false)
      }
    })
  }

  const approveReject = (status: string) => {
    if (!clientRequest || !clientRequest.id || sending) return
    setSending(true)
    const request: IClientRequestStatusDto = {
      status: status,
      comment: comment
    }
    TrinityAPI.changeClientRequestStatus(clientRequest.id, request, response => {
      if (response.status === 200) {
        window.location.reload()
      } else {
        setErrors(response.data)
        setSending(false)
      }
    })
  }

  const blockUnblock = () => {
    const request: IInnRegistryDto = {
      inn: clientRequest?.inn,
      name: clientRequest?.clientCompanyName,
      comment: clientRequest?.comment,
      status: clientRequest?.status === "BLOCKED" ? "APPROVED" : "BLOCKED"
    }
    TrinityAPI.createInnRegistryItem(request, response => {
      if (response.status === 200) {
        window.location.reload()
      }
    })
  }

  const getHumanStatus = (status: string | undefined) => {
    switch (status) {
      case "APPROVED":
        return "подтвержден"
      case "REJECTED":
        return "отклонен"
      case "USED":
        return "с пополнением"
      default:
        return "новый"
    }
  }

  useEffect(() => {
    if (!visible) {
      setClientRequest(undefined)
      setComment("")
    }
  }, [visible])

  useClickAway(modalRef, () => {
    setVisible(false)
  })

  if (visible) {
    return (
      <>
        <section className="popup-forms index-top" id="request_open">
          <div className="container-popup-forms" ref={modalRef}>
            <button className="close-btn-card" id="request_close" onClick={() => setVisible(false)} />
            <div className="popup-card">
              <h4 className="heading-chapter">
                {clientRequest?.id ? (
                  `Рублевый запрос №${clientRequest.id}: ${getHumanStatus(clientRequest.status)}`
                ) : (
                  <>Новый рублевый запрос</>
                )}
              </h4>
              {userStore.isAdmin || userStore.isManager ? (
                <>
                  <fieldset>
                    <legend>ИНН плательщика</legend>
                    <p className="text-form">{clientRequest?.inn}</p>
                    {innRegistry?.status === "APPROVED" ? (
                      <p className="error-form">ИНН добавлен в реестр как одобренный</p>
                    ) : innRegistry?.status === "BLOCKED" ? (
                      <p className="error-form">
                        ИНН заблокирован{innRegistry.comment ? `: ${innRegistry.comment}` : ""}
                      </p>
                    ) : (
                      <></>
                    )}
                  </fieldset>
                  <fieldset>
                    <legend>Название компании плательщика</legend>
                    <p className="text-form">{clientRequest?.clientCompanyName}</p>
                  </fieldset>
                  <fieldset>
                    <legend>Юрисдикция получателя</legend>
                    <p className="text-form">{clientRequest?.recipientCountry?.name?.toUpperCase()}</p>
                  </fieldset>
                  <fieldset>
                    <legend>Юрисдикция банка получателя</legend>
                    <p className="text-form">{clientRequest?.recipientBankCountry?.name?.toUpperCase()}</p>
                  </fieldset>
                  <fieldset>
                    <legend>Сумма</legend>
                    <p className="text-form">
                      {spacedNum(round2Digits(Number(clientRequest?.invoicePrice) / 100))} {clientRequest?.currency}
                    </p>
                  </fieldset>
                  <fieldset>
                    <legend>Email клиента</legend>
                    <p className="text-form">{clientRequest?.user?.username}</p>
                  </fieldset>
                  <fieldset>
                    <legend>Назначение платежа</legend>
                    <p className="text-form">{clientRequest?.paymentPurpose || "-"}</p>
                  </fieldset>
                  <CommonInput
                    typeName={"text"}
                    label={"Комментарий"}
                    placeholder={"Введите причину отказа для клиента"}
                    name={"comment"}
                    value={comment}
                    errorModel={errors}
                    _callback={e => setComment(e)}
                  />
                  <div className="flex-row-justify">
                    <div className="flex-row-24 flex-wrap-mobile">
                      {clientRequest && clientRequest.status === "APPROVED" ? (
                        <></>
                      ) : (
                        <button className="btn" onClick={() => approveReject("APPROVED")}>
                          Подтвердить
                        </button>
                      )}
                      {clientRequest && clientRequest.status === "REJECTED" ? (
                        <></>
                      ) : (
                        <button className="btn grey-btn" onClick={() => approveReject("REJECTED")}>
                          Отклонить запрос
                        </button>
                      )}
                      <button className="btn black-btn" onClick={blockUnblock}>
                        {innRegistry && innRegistry.status === "BLOCKED" ? "Разблокировать ИНН" : "Заблокировать ИНН"}
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <fieldset>
                    <legend>Выберите ИНН</legend>
                    <select
                      value={clientRequest?.inn}
                      onChange={e => handleInnSelectChange(e.target.value)}
                      className="input-form"
                    >
                      <option value="">Выберите ИНН</option>
                      {innList?.items.map((i, index) => (
                        <option value={i.inn} key={index}>
                          {i.inn} {i.name}
                        </option>
                      ))}
                    </select>
                    {getFieldError(errors, "inn") ? (
                      <p className="error-form">{getFieldError(errors, "inn")}</p>
                    ) : (
                      <></>
                    )}
                  </fieldset>
                  <fieldset>
                    <legend>Выберите контрагента</legend>
                    <select onChange={e => handleContractorSelectChange(e.target.value)} className="input-form">
                      <option value="">Выберите Контрагента</option>
                      {contractors?.map((i, index) => (
                        <option value={i.id} key={index}>
                          {i.name}
                        </option>
                      ))}
                    </select>
                  </fieldset>
                  <CommonInput
                    typeName={"text"}
                    label={"Название компании плательщика"}
                    placeholder={'ООО "Компания"'}
                    name={"clientCompanyName"}
                    value={clientRequest?.clientCompanyName}
                    errorModel={errors}
                    _callback={e =>
                      setClientRequest({
                        ...clientRequest,
                        clientCompanyName: e
                      })
                    }
                  />
                  <CountryPicker
                    label={"Юрисдикция получателя"}
                    size={1}
                    name={"recipientCountry"}
                    value={clientRequest?.recipientCountry ? new Array(clientRequest.recipientCountry) : []}
                    errorModel={errors}
                    _callback={changeRecipientCountry}
                  />
                  <CountryPicker
                    label={"Юрисдикция банка получателя"}
                    size={1}
                    name={"recipientBankCountry"}
                    value={clientRequest?.recipientBankCountry ? new Array(clientRequest.recipientBankCountry) : []}
                    errorModel={errors}
                    _callback={changeRecipientBankCountry}
                  />
                  <CommonInput
                    typeName={"number"}
                    label={"Сумма"}
                    placeholder={"В валюте получателя"}
                    name={"invoicePrice"}
                    value={clientRequest?.invoicePrice}
                    errorModel={errors}
                    _callback={e => setClientRequest({ ...clientRequest, invoicePrice: e })}
                  />
                  <CurrencyPicker
                    label={"Валюта"}
                    name={"currency"}
                    errorModel={errors}
                    initValue={clientRequest?.currency}
                    _callback={e => setClientRequest({ ...clientRequest, currency: e })}
                  />
                  <CommonInput
                    typeName="string"
                    label="Назначение платежа"
                    placeholder="Назначение платежа"
                    name="paymentPurpose"
                    value={clientRequest?.paymentPurpose}
                    errorModel={errors}
                    _callback={e => setClientRequest({ ...clientRequest, paymentPurpose: e })}
                  />
                  <div className="flex-row-24">
                    <button className="btn" onClick={submit}>
                      {sending ? "Подождите..." : "Создать"}
                    </button>
                    <button className="btn grey-btn" onClick={() => setVisible(false)}>
                      Отменить
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </section>
      </>
    )
  } else {
    return <></>
  }
}
